import React, { useState, useRef, useEffect } from 'react';
import {
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronDown,
  FaCheck
} from 'react-icons/fa';

/**
 * Modular sorting dropdown component focused exclusively on managing data sort options and order direction.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} [props.options=[]] - List of sort options (e.g., `[{ value: 'name', label: 'Name' }]`).
 * @param {string} [props.sortLabel='Sort By'] - Fallback label displayed when no sort option is active.
 * @param {string} [props.sortValue] - Controlled value of the active sort option.
 * @param {string} [props.defaultSortValue=''] - Initial sort option value for uncontrolled usage.
 * @param {'asc'|'desc'} [props.sortOrder] - Controlled sort direction ('asc' or 'desc').
 * @param {'asc'|'desc'} [props.defaultSortOrder='asc'] - Initial sort direction for uncontrolled usage.
 * @param {Function} [props.onSortChange] - Callback invoked when sort option or direction changes: `({ by, order }) => void`.
 * @param {boolean} [props.showSortOrderToggle=true] - Displays the Ascending/Descending toggle button beside the dropdown when active.
 * @param {string} [props.className=''] - Additional CSS classes applied to the container element.
 *
 * @example
 * <DataSorting
 *   options={sortOptions}
 *   sortValue={sort}
 *   sortOrder={order}
 *   onSortChange={({ by, order }) => handleSortChange(by, order)}
 * />
 */
const DataSorting = ({
  options = [],
  sortLabel = 'Sort By',
  sortValue: controlledSortValue,
  defaultSortValue = '',
  sortOrder: controlledSortOrder,
  defaultSortOrder = 'asc',
  onSortChange,
  showSortOrderToggle = true,
  className = ''
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  const isControlledSort = controlledSortValue !== undefined;
  const [internalSortValue, setInternalSortValue] = useState(defaultSortValue);
  const activeSortValue = isControlledSort ? controlledSortValue : internalSortValue;

  const isControlledSortOrder = controlledSortOrder !== undefined;
  const [internalSortOrder, setInternalSortOrder] = useState(defaultSortOrder);
  const activeSortOrder = isControlledSortOrder ? controlledSortOrder : internalSortOrder;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleSortSelect = (newSortBy) => {
    if (!isControlledSort) {
      setInternalSortValue(newSortBy);
    }
    if (typeof onSortChange === 'function') {
      onSortChange({ by: newSortBy, order: activeSortOrder });
    }
    setIsDropdownOpen(false);
  };

  const handleSortOrderToggle = () => {
    const nextOrder = activeSortOrder === 'asc' ? 'desc' : 'asc';
    if (!isControlledSortOrder) {
      setInternalSortOrder(nextOrder);
    }
    if (typeof onSortChange === 'function') {
      onSortChange({ by: activeSortValue, order: nextOrder });
    }
  };

  if (!options || options.length === 0) {
    return null;
  }

  const currentOption = options.find((opt) => {
    const optValue = opt.value !== undefined ? opt.value : opt.key;
    return optValue === activeSortValue;
  });

  const currentLabel = currentOption
    ? currentOption.label || currentOption.name
    : sortLabel;

  return (
    <div
      className={`relative inline-flex items-center gap-1.5 ${className}`.trim()}
      ref={containerRef}
    >
      <button
        type="button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold transition-colors shadow-2xs focus:outline-none focus:ring-2 focus:ring-[#3B74BE]/20 cursor-pointer ${
          activeSortValue
            ? 'border-[#3B74BE] bg-blue-50 text-[#3B74BE]'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        <FaSort className={activeSortValue ? 'text-[#3B74BE]' : 'text-gray-400'} />
        <span>{currentLabel}</span>
        <FaChevronDown
          className={`h-3 w-3 text-gray-400 transition-transform duration-200 ${
            isDropdownOpen ? 'rotate-180 text-[#3B74BE]' : ''
          }`}
        />
      </button>

      {activeSortValue && showSortOrderToggle && (
        <button
          type="button"
          title={
            activeSortOrder === 'asc'
              ? 'Ascending Order (Click for Descending)'
              : 'Descending Order (Click for Ascending)'
          }
          onClick={handleSortOrderToggle}
          className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-xs text-gray-700 hover:bg-gray-50 shadow-2xs transition-colors cursor-pointer"
        >
          {activeSortOrder === 'asc' ? (
            <FaSortUp className="w-3.5 h-3.5 translate-y-0.5 text-[#3B74BE]" />
          ) : (
            <FaSortDown className="w-3.5 h-3.5 -translate-y-0.5 text-[#3B74BE]" />
          )}
        </button>
      )}

      {isDropdownOpen && (
        <div className="absolute left-0 z-50 mt-1.5 w-56 rounded-md border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 top-full">
          <div className="p-2 border-b border-gray-200 bg-gray-50 rounded-t-md">
            <span className="text-xs font-semibold text-gray-700">Sort By</span>
          </div>
          <div className="p-1 divide-y divide-gray-100 max-h-56 overflow-y-auto">
            {options.map((opt) => {
              const optVal = opt.value !== undefined ? opt.value : opt.key;
              const optLabel = opt.label || opt.name || optVal;
              const isSelected = optVal === activeSortValue;

              return (
                <div
                  key={optVal}
                  onClick={() => handleSortSelect(optVal)}
                  className={`flex items-center justify-between px-3 py-2 text-xs rounded-md cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-blue-50 text-[#3B74BE] font-medium'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <span className="truncate">{optLabel}</span>
                  {isSelected && <FaCheck className="text-[10px] text-[#3B74BE]" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSorting;
